import { useEffect, useMemo, useRef } from 'react';
import { Play } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { RunCard } from '@/pages/schedules/components/run-card';
import { ResizeHandle } from '@/pages/schedules/components/resize-handle';
import { groupRunsByDay } from '@/pages/schedules/helpers/group-runs';
import type { OrgSchedule, ScheduledRun } from '@/types/schedules';

interface RunsFeedPanelProps {
  width: number;
  onResizeStart: (e: React.MouseEvent) => void;
  runs: ScheduledRun[];
  isLoading: boolean;
  isError: boolean;
  selectedScheduleId: string | null;
  schedules: OrgSchedule[];
  selectedRunId: string | null;
  onSelectRun: (id: string) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

export function RunsFeedPanel({
  width,
  onResizeStart,
  runs,
  isLoading,
  isError,
  selectedScheduleId,
  schedules,
  selectedRunId,
  onSelectRun,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: RunsFeedPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          onLoadMore();
        }
      },
      { root: scrollRef.current, rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore]);
  const scopeLabel = useMemo(() => {
    if (!selectedScheduleId) return 'All Schedules';
    const match = schedules.find((s) => s.id === selectedScheduleId);
    return match?.name || match?.workflow_name || 'Schedule';
  }, [selectedScheduleId, schedules]);

  const groups = useMemo(() => groupRunsByDay(runs), [runs]);

  return (
    <div
      className='relative flex h-full shrink-0 flex-col bg-sidebar/50'
      style={{ width }}
    >
      <div className='flex h-13 items-center gap-2 px-6 py-3'>
        <Play className='size-4' />
        <span className='text-sm font-medium'>Runs</span>
        <span className='size-0.5 rounded-full bg-muted-foreground' />
        <span className='text-sm text-muted-foreground'>{scopeLabel}</span>
      </div>
      <Separator />

      <div
        ref={scrollRef}
        className='min-h-0 flex-1 overflow-y-auto subtle-scrollbar'
      >
        {isLoading && (
          <div className='space-y-4 p-6'>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className='h-40 rounded-lg' />
            ))}
          </div>
        )}
        {isError && (
          <p className='p-6 text-center text-sm text-muted-foreground'>
            Failed to load runs.
          </p>
        )}
        {!isLoading && !isError && runs.length === 0 && (
          <p className='p-6 text-center text-sm text-muted-foreground'>
            No scheduled runs yet.
          </p>
        )}
        {groups.map((group, i) => (
          <div key={group.key}>
            {i > 0 && <Separator />}
            <div className='px-6 pb-4 pt-6 text-sm text-muted-foreground'>
              {group.label}
            </div>
            <div className='space-y-6 px-6 pb-6'>
              {group.runs.map((run) => (
                <RunCard
                  key={run.id}
                  run={run}
                  selected={selectedRunId === run.id}
                  onSelect={() => onSelectRun(run.id)}
                />
              ))}
            </div>
          </div>
        ))}

        {(hasNextPage || isFetchingNextPage) && (
          <div ref={sentinelRef} className='space-y-6 px-6 pb-6'>
            {isFetchingNextPage &&
              Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className='h-24 rounded-lg' />
              ))}
          </div>
        )}
      </div>

      <ResizeHandle onResizeStart={onResizeStart} />
    </div>
  );
}
